<template>
  <div>
    <v-container fluid class="pa-2">
      <!-- Filters Card -->
      <v-card class="mb-4">
        <v-card-title>
          <v-icon left>filter_list</v-icon>
          Filters
        </v-card-title>
        <v-card-text>
          <v-layout row wrap>
            <v-flex xs12 md3>
              <v-select
                v-model="filters.table_name"
                :items="tableOptions"
                label="Table"
                clearable
                prepend-icon="table_chart"
                @change="loadAuditLogs"
              ></v-select>
            </v-flex>
            <v-flex xs12 md3>
              <v-select
                v-model="filters.action"
                :items="actionOptions"
                label="Action"
                clearable
                prepend-icon="build"
                @change="loadAuditLogs"
              ></v-select>
            </v-flex>
            <v-flex xs12 md3>
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="filters.start_date"
                    label="Start Date"
                    prepend-icon="event"
                    readonly
                    v-on="on"
                    clearable
                    @click:clear="filters.start_date = null; loadAuditLogs()"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="filters.start_date"
                  @input="startDateMenu = false; loadAuditLogs()"
                ></v-date-picker>
              </v-menu>
            </v-flex>
            <v-flex xs12 md3>
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="filters.end_date"
                    label="End Date"
                    prepend-icon="event"
                    readonly
                    v-on="on"
                    clearable
                    @click:clear="filters.end_date = null; loadAuditLogs()"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="filters.end_date"
                  @input="endDateMenu = false; loadAuditLogs()"
                ></v-date-picker>
              </v-menu>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>

      <!-- Main Data Table Card -->
      <v-card>
        <v-card-title>
          <v-icon left>history</v-icon>
          {{ isAdmin ? 'All Actions' : 'My Actions' }}
          <v-spacer></v-spacer>
<!--          <v-text-field
            v-model="search"
            append-icon="search"
            label="Search"
            single-line
            hide-details
            @input="searchAuditLogs"
          ></v-text-field>-->
        </v-card-title>

        <v-data-table
          :headers="headers"
          :items="auditLogs"
          :loading="loading"
          :pagination.sync="pagination"
          :total-items="totalItems"
          :rows-per-page-items="[10, 25, 50, 100]"
          class="elevation-1"
          @update:pagination="loadAuditLogs"
        >
          <template v-slot:items="props">
            <tr @click="viewDetails(props.item)">
              <td>{{ formatDate(props.item.created_at) }}</td>
              <td>{{ props.item.table_name }}</td>
              <td>
                <v-chip
                  :color="getActionColor(props.item.action)"
                  text-color="white"
                  small
                >
                  {{ props.item.action }}
                </v-chip>
              </td>
              <td>{{ props.item.record_id }}</td>
              <td v-if="isAdmin">{{ props.item.user_name || 'Unknown' }}</td>
              <td>{{ props.item.remarks || '-' }}</td>
            </tr>
          </template>

          <template v-slot:no-data>
            <v-alert :value="true" color="info" icon="info">
              No audit logs found.
            </v-alert>
          </template>
        </v-data-table>
      </v-card>

      <!-- Details Dialog -->
      <v-dialog v-model="detailsDialog" max-width="800px">
        <v-card>
          <v-card-title>
            <v-icon left>visibility</v-icon>
            Action Details
            <v-spacer></v-spacer>
            <v-btn icon @click="detailsDialog = false">
              <v-icon>close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text v-if="selectedItem">
            <v-layout column>
              <v-flex>
                <strong>Date:</strong> {{ formatDate(selectedItem.created_at) }}
              </v-flex>
              <v-flex>
                <strong>Table:</strong> {{ selectedItem.table_name }}
              </v-flex>
              <v-flex>
                <strong>Action:</strong> {{ selectedItem.action }}
              </v-flex>
              <v-flex>
                <strong>Record ID:</strong> {{ selectedItem.record_id }}
              </v-flex>
              <v-flex v-if="isAdmin">
                <strong>User:</strong> {{ selectedItem.user_name || 'Unknown' }}
              </v-flex>
              <v-flex v-if="selectedItem.remarks">
                <strong>Remarks:</strong> {{ selectedItem.remarks }}
              </v-flex>

              <!-- Old Values -->
              <v-flex v-if="selectedItem.old_values" class="mt-3">
                <strong>Old Values:</strong>
                <pre class="code-block">{{ formatJson(selectedItem.old_values) }}</pre>
              </v-flex>

              <!-- New Values -->
              <v-flex v-if="selectedItem.new_values" class="mt-3">
                <strong>New Values:</strong>
                <pre class="code-block">{{ formatJson(selectedItem.new_values) }}</pre>
              </v-flex>

              <!-- Changed Columns -->
              <v-flex v-if="selectedItem.changed_columns && selectedItem.changed_columns.length > 0" class="mt-3">
                <strong>Changed Fields:</strong>
                <v-chip
                  v-for="column in selectedItem.changed_columns"
                  :key="column"
                  small
                  class="ma-1"
                >
                  {{ column }}
                </v-chip>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { client } from '@/services'

export default {
  name: 'Actions',
  data() {
    return {
      loading: false,
      auditLogs: [],
      totalItems: 0,
      search: '',
      searchTimeout: null,
      startDateMenu: false,
      endDateMenu: false,
      detailsDialog: false,
      selectedItem: null,

      pagination: {
        page: 1,
        rowsPerPage: 25,
        sortBy: 'created_at',
        descending: true
      },

      filters: {
        table_name: null,
        action: null,
        start_date: null,
        end_date: null
      },

      tableOptions: [
        { text: 'Users', value: 'users' },
        { text: 'Devices', value: 'devices' },
        { text: 'Groups', value: 'groups' },
        { text: 'Assets', value: 'assets' },
        { text: 'Schedules', value: 'schedules' },
        { text: 'Playlists', value: 'playlists' }
      ],

      actionOptions: [
        { text: 'Insert', value: 'INSERT' },
        { text: 'Update', value: 'UPDATE' },
        { text: 'Delete', value: 'DELETE' }
      ]
    }
  },

  computed: {
    ...mapGetters('users', ['GET_USER']),

    currentUser() {
      return this.GET_USER;
    },

    isAdmin() {
      const role = this.currentUser.role || "user";
      return role === 'admin' || role === 'super_user' || role === 'root';
    },

    headers() {
      const baseHeaders = [
        { text: 'Date', value: 'created_at', sortable: true },
        { text: 'Table', value: 'table_name', sortable: true },
        { text: 'Action', value: 'action', sortable: true },
        { text: 'Record ID', value: 'record_id', sortable: false }
      ];

      if (this.isAdmin) {
        baseHeaders.push({ text: 'User', value: 'user_name', sortable: true });
      }

      baseHeaders.push({ text: 'Remarks', value: 'remarks', sortable: false });

      return baseHeaders;
    }
  },
  mounted() {
    this.loadAuditLogs();
  },

  methods: {
    async loadAuditLogs() {
      this.loading = true;

      try {
        const params = {
          $limit: this.pagination.rowsPerPage,
          $skip: (this.pagination.page - 1) * this.pagination.rowsPerPage
        };

        // Add filters
        Object.keys(this.filters).forEach(key => {
          if (this.filters[key] !== null && this.filters[key] !== undefined && this.filters[key] !== '') {
            // Format dates for API if needed
            if ((key === 'start_date' || key === 'end_date') && this.filters[key]) {
              params[key] = new Date(this.filters[key]).toISOString();
            } else {
              params[key] = this.filters[key];
            }
          }
        });

        // Add search
        if (this.search.trim()) {
          params.search = this.search.trim();
        }

        // Add sorting
        if (this.pagination.sortBy) {
          params.$sort = {
            [this.pagination.sortBy]: this.pagination.descending ? -1 : 1
          };
        }

        const response = await client.service('audit-logs').find({ query: params });

        this.auditLogs = response.data;
        this.totalItems = response.total;

      } catch (error) {
        console.error('Error loading audit logs:', error);
        this.$toast.error('Failed to load audit logs');
      } finally {
        this.loading = false;
      }
    },

    searchAuditLogs() {
      // Debounce search to avoid too many API calls
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1; // Reset to first page
        this.loadAuditLogs();
      }, 500);
    },

    viewDetails(item) {
      this.selectedItem = item;
      this.detailsDialog = true;
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString();
    },

    formatJson(data) {
      if (typeof data === 'string') {
        try {
          return JSON.stringify(JSON.parse(data), null, 2);
        } catch(err) {
          return data;
        }
      }
      return JSON.stringify(data, null, 2);
    },

    getActionColor(action) {
      switch (action) {
        case 'INSERT': return 'green';
        case 'UPDATE': return 'orange';
        case 'DELETE': return 'red';
        default: return 'grey';
      }
    }
  }
}
</script>

<style scoped>
.code-block {
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 10px;
  font-size: 12px;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
}
</style>



// WEBPACK FOOTER //
// src/pages/Actions.vue